import type { SxProps, Theme } from '@mui/material/styles'
import { LAYOUT_APP_BAR_HEIGHT_PX } from '@/components/layout/layoutConstants'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'

/** Chiều rộng modal theo viewport — ưu tiên tận dụng màn hình. */
export const FORM_MODAL_VIEWPORT_WIDTH = '90vw' as const

/**
 * Neo sát dưới thanh header menu — overlay/paper không che menu.
 * Trùng `LAYOUT_APP_BAR_HEIGHT_PX` (44px).
 */
export const FORM_MODAL_VIEWPORT_TOP_OFFSET_PX = LAYOUT_APP_BAR_HEIGHT_PX

/** Đệm dưới — tránh modal chạm mép bottom khi cao. */
export const FORM_MODAL_VIEWPORT_BOTTOM_BUFFER_PX = 10

/** Chiều cao tối đa modal (dưới header + đệm đáy). */
export const FORM_MODAL_VIEWPORT_MAX_HEIGHT =
  `calc(100dvh - ${FORM_MODAL_VIEWPORT_TOP_OFFSET_PX + FORM_MODAL_VIEWPORT_BOTTOM_BUFFER_PX}px)` as const

/** Overlay + backdrop bắt đầu dưới header — header vẫn hiện và bấm được. */
export const formModalBelowHeaderOverlaySx = {
  top: FORM_MODAL_VIEWPORT_TOP_OFFSET_PX,
} as const

/** Mỗi entry cấu hình kích thước modal. */
type FormModalSizeEntry = {
  maxWidthPx: number
  widthCss?: string
}

/** Trần width (px) theo loại form — kết hợp với widthCss qua `maxWidth`. */
export const FORM_MODAL_SIZE: Readonly<Record<string, FormModalSizeEntry>> = {
  compact: { maxWidthPx: 520 },
  standard: { maxWidthPx: 960 },
  wide: { maxWidthPx: 1200 },
  xwide: { maxWidthPx: 1560 },
  xxwide: { maxWidthPx: 1800 },
  fullscreen: { maxWidthPx: 2560, widthCss: '98vw' },
}

export type FormModalSize = keyof typeof FORM_MODAL_SIZE

/** Khoảng cách grid trong modal. */
export const formModalGridSpacing = { xs: 1, sm: 1.25 } as const

/** Tiêu đề section gọn — dùng cho form nhiều trường trong modal. */
export const formModalSectionTitleCompactSx: SxProps<Theme> = {
  mt: 1.25,
  mb: 0.25,
  color: banhangKiotLike.kiotFormLabelColor,
  fontWeight: 600,
  fontSize: `${banhangKiotLike.kiotFormLabelFontSizePx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  lineHeight: banhangKiotLike.kiotLineHeight,
  display: 'block',
  '&:first-of-type': { mt: 0 },
}

export const formModalSectionTitleSx: SxProps<Theme> = {
  mt: 1,
  mb: 0.25,
  color: banhangKiotLike.kiotFormLabelColor,
  fontWeight: 600,
  fontSize: `${banhangKiotLike.kiotFormLabelFontSizePx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  lineHeight: banhangKiotLike.kiotLineHeight,
  display: 'block',
}

export const formGridColThird = { xs: 12, sm: 6, md: 4 } as const
export const formGridColHalf = { xs: 12, sm: 6 } as const
export const formGridColFull = { xs: 12 } as const
export const formGridColQuarter = { xs: 12, sm: 6, md: 3 } as const

/** CSS width cho `BaseModal` — mặc định `90vw`, không vượt trần theo size. */
export function getFormModalWidthCss(size: FormModalSize = 'standard'): string {
  return FORM_MODAL_SIZE[size].widthCss ?? FORM_MODAL_VIEWPORT_WIDTH
}

export function getFormModalMaxWidthPx(size: FormModalSize): number {
  return FORM_MODAL_SIZE[size].maxWidthPx
}

/** @deprecated dùng `getFormModalMaxWidthPx` — giữ tương thích import cũ. */
export function getFormModalWidthPx(size: FormModalSize): number {
  return getFormModalMaxWidthPx(size)
}

export function getFormModalPaperMaxHeight(_size?: FormModalSize): string {
  return FORM_MODAL_VIEWPORT_MAX_HEIGHT
}
